import { produce } from "immer";
import {
  CLEAR_CARE_HEARTH_FEEDBACK,
  CREATE_CARE_HEARTH_OFFERS,
  CREATE_CARE_HEARTH_OFFERS_FAILURE,
  CREATE_CARE_HEARTH_OFFERS_SUCCESS,
  DECIDE_CARE_HEARTH_DISPUTE,
  DECIDE_CARE_HEARTH_DISPUTE_FAILURE,
  DECIDE_CARE_HEARTH_DISPUTE_SUCCESS,
  FETCH_CARE_HEARTH_ENQUIRIES,
  FETCH_CARE_HEARTH_ENQUIRIES_FAILURE,
  FETCH_CARE_HEARTH_ENQUIRIES_SUCCESS,
  FETCH_CARE_HEARTH_ENQUIRY,
  FETCH_CARE_HEARTH_ENQUIRY_FAILURE,
  FETCH_CARE_HEARTH_ENQUIRY_SUCCESS,
  FETCH_CARE_HEARTH_SEARCHES,
  FETCH_CARE_HEARTH_SEARCHES_FAILURE,
  FETCH_CARE_HEARTH_SEARCHES_SUCCESS,
  FETCH_CARE_HEARTH_AUDIT_EVENTS,
  FETCH_CARE_HEARTH_AUDIT_EVENTS_FAILURE,
  FETCH_CARE_HEARTH_AUDIT_EVENTS_SUCCESS,
  FIND_CARE_HEARTH_ELIGIBLE_HOMES,
  FIND_CARE_HEARTH_ELIGIBLE_HOMES_FAILURE,
  FIND_CARE_HEARTH_ELIGIBLE_HOMES_SUCCESS,
  RECORD_CARE_HEARTH_SEARCH_CALL,
  RECORD_CARE_HEARTH_SEARCH_CALL_FAILURE,
  RECORD_CARE_HEARTH_SEARCH_CALL_SUCCESS,
  START_CARE_HEARTH_SEARCH_ENQUIRY,
  START_CARE_HEARTH_SEARCH_ENQUIRY_FAILURE,
  START_CARE_HEARTH_SEARCH_ENQUIRY_SUCCESS,
  UPDATE_CARE_HEARTH_LIFECYCLE,
  UPDATE_CARE_HEARTH_LIFECYCLE_FAILURE,
  UPDATE_CARE_HEARTH_LIFECYCLE_SUCCESS,
  UPDATE_CARE_HEARTH_VERIFICATION,
  UPDATE_CARE_HEARTH_VERIFICATION_FAILURE,
  UPDATE_CARE_HEARTH_VERIFICATION_SUCCESS,
} from "./constants";

export const initialState = {
  audit: [],
  // Queue sizes for the list's tabs, e.g. { awaiting_family: 3 }.
  counts: null,
  detail: null,
  detailError: "",
  isFetchingAudit: false,
  isFetchingDetail: false,
  isFetchingList: false,
  isFindingEligible: false,
  isSaving: false,
  lastCompletedAction: null,
  listError: "",
  listItems: [],
  match: null,
  matchError: "",
  mutationError: "",
  pagination: { last_page: 1, total: 0 },
  // Not sent (15 October 2026): /find searches with no enquiry, for our team
  // to call.
  isFetchingSearches: false,
  searchError: "",
  searchItems: [],
  searchPagination: { last_page: 1, total: 0 },
};

// A search left the Not sent queue: drop its row and take it off the counts.
const removeSearch = (draft, uuid) => {
  const index = draft.searchItems.findIndex((item) => item.uuid === uuid);
  if (index === -1) return;

  draft.searchItems.splice(index, 1);
  draft.searchPagination.total = Math.max(
    0,
    (draft.searchPagination.total || 0) - 1,
  );
  if (typeof draft.counts?.not_sent === "number") {
    draft.counts.not_sent = Math.max(0, draft.counts.not_sent - 1);
  }
};

const CareHearthReducer = (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case FETCH_CARE_HEARTH_ENQUIRIES:
        draft.isFetchingList = true;
        draft.listError = "";
        break;
      case FETCH_CARE_HEARTH_ENQUIRIES_SUCCESS:
        draft.isFetchingList = false;
        draft.listItems = action.payload.items;
        draft.pagination = action.payload.pagination;
        draft.counts = action.payload.counts;
        break;
      case FETCH_CARE_HEARTH_ENQUIRIES_FAILURE:
        draft.isFetchingList = false;
        draft.listError = action.payload;
        break;

      case FETCH_CARE_HEARTH_SEARCHES:
        draft.isFetchingSearches = true;
        draft.searchError = "";
        break;
      case FETCH_CARE_HEARTH_SEARCHES_SUCCESS:
        draft.isFetchingSearches = false;
        draft.searchItems = action.payload.items;
        draft.searchPagination = action.payload.pagination;
        // Keeps the tab's count fresh without reloading the enquiries.
        if (typeof action.payload.counts?.not_sent === "number") {
          draft.counts = {
            ...(draft.counts || {}),
            not_sent: action.payload.counts.not_sent,
          };
        }
        break;
      case FETCH_CARE_HEARTH_SEARCHES_FAILURE:
        draft.isFetchingSearches = false;
        draft.searchError = action.payload;
        break;

      case FETCH_CARE_HEARTH_ENQUIRY:
        draft.isFetchingDetail = true;
        draft.detail = null;
        draft.audit = [];
        draft.detailError = "";
        break;
      case FETCH_CARE_HEARTH_ENQUIRY_SUCCESS:
        draft.isFetchingDetail = false;
        draft.detail = action.payload.enquiry;
        draft.audit = action.payload.audit;
        break;
      case FETCH_CARE_HEARTH_ENQUIRY_FAILURE:
        draft.isFetchingDetail = false;
        draft.detailError = action.payload;
        break;

      case FETCH_CARE_HEARTH_AUDIT_EVENTS:
        draft.isFetchingAudit = true;
        break;
      case FETCH_CARE_HEARTH_AUDIT_EVENTS_SUCCESS:
        draft.isFetchingAudit = false;
        draft.audit = action.payload;
        break;
      case FETCH_CARE_HEARTH_AUDIT_EVENTS_FAILURE:
        draft.isFetchingAudit = false;
        break;

      case FIND_CARE_HEARTH_ELIGIBLE_HOMES:
        draft.isFindingEligible = true;
        draft.match = null;
        draft.matchError = "";
        break;
      case FIND_CARE_HEARTH_ELIGIBLE_HOMES_SUCCESS:
        draft.isFindingEligible = false;
        draft.match = action.payload;
        break;
      case FIND_CARE_HEARTH_ELIGIBLE_HOMES_FAILURE:
        draft.isFindingEligible = false;
        draft.matchError = action.payload;
        break;

      case CREATE_CARE_HEARTH_OFFERS:
      case DECIDE_CARE_HEARTH_DISPUTE:
      case RECORD_CARE_HEARTH_SEARCH_CALL:
      case START_CARE_HEARTH_SEARCH_ENQUIRY:
      case UPDATE_CARE_HEARTH_LIFECYCLE:
      case UPDATE_CARE_HEARTH_VERIFICATION:
        draft.isSaving = true;
        draft.mutationError = "";
        draft.lastCompletedAction = null;
        break;
      case CREATE_CARE_HEARTH_OFFERS_SUCCESS:
        draft.isSaving = false;
        draft.match = null;
        draft.lastCompletedAction = action.payload;
        break;
      case DECIDE_CARE_HEARTH_DISPUTE_SUCCESS:
      case UPDATE_CARE_HEARTH_LIFECYCLE_SUCCESS:
      case UPDATE_CARE_HEARTH_VERIFICATION_SUCCESS:
        draft.isSaving = false;
        draft.lastCompletedAction = action.payload;
        break;
      // Wrong number or not interested takes the search out of the queue.
      case RECORD_CARE_HEARTH_SEARCH_CALL_SUCCESS: {
        const { item, message, type } = action.payload;
        draft.isSaving = false;
        draft.lastCompletedAction = {
          leftQueue: item?.in_queue === false,
          message,
          type,
        };
        if (!item?.uuid) break;
        if (item.in_queue === false) {
          removeSearch(draft, item.uuid);
          break;
        }
        const index = draft.searchItems.findIndex(
          (row) => row.uuid === item.uuid,
        );
        if (index !== -1) draft.searchItems[index] = item;
        break;
      }
      // The search now has an enquiry, so it is no longer Not sent.
      case START_CARE_HEARTH_SEARCH_ENQUIRY_SUCCESS:
        draft.isSaving = false;
        draft.lastCompletedAction = action.payload;
        removeSearch(draft, action.payload.searchUuid);
        break;
      case CREATE_CARE_HEARTH_OFFERS_FAILURE:
      case DECIDE_CARE_HEARTH_DISPUTE_FAILURE:
      case RECORD_CARE_HEARTH_SEARCH_CALL_FAILURE:
      case START_CARE_HEARTH_SEARCH_ENQUIRY_FAILURE:
      case UPDATE_CARE_HEARTH_LIFECYCLE_FAILURE:
      case UPDATE_CARE_HEARTH_VERIFICATION_FAILURE:
        draft.isSaving = false;
        draft.mutationError = action.payload;
        break;

      case CLEAR_CARE_HEARTH_FEEDBACK:
        draft.lastCompletedAction = null;
        draft.mutationError = "";
        break;
      default:
        break;
    }
  });

export default CareHearthReducer;
